import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { notifyModalOpen, notifyModalClose } from '../utils/modalState';

interface SuperModalPortalProps {
  children: React.ReactNode;
  onClose: () => void;
}

const SuperModalPortal: React.FC<SuperModalPortalProps> = ({ children, onClose }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    notifyModalOpen();

    return () => {
      setMounted(false);
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
      notifyModalClose();
    };
  }, []);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking directly on the backdrop
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!mounted) return null;

  return createPortal(
    <div 
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 99999, // Extremely high z-index
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
    >
      {children}
    </div>,
    document.body
  );
};

export default SuperModalPortal; 